import { useState, useEffect, useCallback, useMemo } from 'react'
import { LandlordDashboardState, LandlordStats, Tenancy } from '@/types/landlord'
import { Property } from '@/types'
import { getApiUrl } from '@/lib/apiUrl'

interface UseLandlordDataProps {
  userId: string
  selectedPropertyId?: string | null
}

export function useLandlordData({ userId, selectedPropertyId }: UseLandlordDataProps) {
  const [state, setState] = useState<Omit<LandlordDashboardState, 'stats'> & { tenancies: Tenancy[]; loading: LandlordDashboardState['loading'] & { tenancies?: boolean } }>({
    properties: [],
    applications: [],
    payments: [],
    tenancies: [],
    loading: {
      properties: true,
      applications: true,
      payments: true,
      tenancies: false
    },
    error: null
  })

  /** ============================
   * LOAD FUNCTIONS
   * ============================ */
  const loadProperties = useCallback(async () => {
    if (!userId) return
    setState(prev => ({ ...prev, loading: { ...prev.loading, properties: true }, error: null }))

    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      // Use the landlord-specific endpoint to get properties for this user
      const response = await fetch(`${apiUrl}/properties/landlord/${userId}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      })
      if (!response.ok) throw new Error(`Failed to load properties: ${response.statusText}`)
      const data = await response.json()

      // The response structure is { properties: [...], pagination: {...} }
      const propertiesArray = data.properties || data || []
      
      // Transform backend data to frontend format (map _id to id)
      const properties = propertiesArray.map((prop: any) => {
        // Build location string from address object if location is not a string
        let location = prop.location
        if (!location && prop.address && typeof prop.address === 'object') {
          const addr = prop.address
          const parts = []
          if (addr.street) parts.push(addr.street)
          if (addr.city) parts.push(addr.city)
          if (addr.state) parts.push(addr.state)
          if (parts.length > 0) {
            location = parts.join(', ')
          }
        }
        
        return {
          ...prop,
          id: prop._id || prop.id,
          landlord_id: prop.landlord?._id || prop.landlord || prop.landlord_id,
          // Extract and normalize image URLs if images is an array of objects
          images: (() => {
            const imageUrls = prop.images?.map((img: any) => typeof img === 'string' ? img : img.url) || prop.images || [];
            // Normalize localhost URLs to use backend URL from environment
            return imageUrls.map((url: string) => {
              if (!url) return url;
              if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
                const apiUrl = getApiUrl();
                const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
                try {
                  const urlObj = new URL(url);
                  return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
                } catch {
                  return url.replace(/https?:\/\/[^/]+/, apiBaseUrl);
                }
              } else if (url.startsWith('/uploads')) {
                const apiUrl = getApiUrl();
                const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
                return `${apiBaseUrl}${url}`;
              }
              return url;
            });
          })(),
          // Add location if it was built from address
          location: location || prop.location || '',
          // Ensure duration is available from leaseTerms for long-term rentals
          duration: prop.duration || prop.leaseTerms?.minLease || prop.duration,
          // Preserve leaseTerms for duration lookup
          leaseTerms: prop.leaseTerms,
          // Extract house documents URLs
          house_documents: (() => {
            if (prop.houseDocuments && Array.isArray(prop.houseDocuments) && prop.houseDocuments.length > 0) {
              return prop.houseDocuments.map((doc: any) => {
                if (typeof doc === 'string') return doc
                if (doc && typeof doc === 'object' && doc.url) return doc.url
                return doc
              })
            }
            if (prop.house_documents && Array.isArray(prop.house_documents) && prop.house_documents.length > 0) {
              return prop.house_documents.map((doc: any) => {
                if (typeof doc === 'string') return doc
                if (doc && typeof doc === 'object' && doc.url) return doc.url
                return doc
              })
            }
            return []
          })(),
          // Map isVerified from backend (might be isVerified or is_verified)
          is_verified: prop.isVerified !== undefined ? prop.isVerified : prop.is_verified,
          is_available: prop.isAvailable !== undefined ? prop.isAvailable : prop.is_available
        }
      })

      setState(prev => ({
        ...prev,
        properties: properties,
        loading: { ...prev.loading, properties: false }
      }))
    } catch (error) {
      console.error('Error loading properties:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load properties',
        loading: { ...prev.loading, properties: false }
      }))
    }
  }, [userId])

  const loadApplications = useCallback(async (propertyId?: string) => {
    if (!userId) return
    setState(prev => ({ ...prev, loading: { ...prev.loading, applications: true }, error: null }))

    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      // Use the property-specific endpoint if propertyId is provided, otherwise get all landlord applications
      const url = propertyId
        ? `${apiUrl}/applications/property/${propertyId}`
        : `${apiUrl}/applications`
      const response = await fetch(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      })
      if (!response.ok) throw new Error(`Failed to load applications: ${response.statusText}`)
      const data = await response.json()

      // The response structure is { applications: [...], pagination: {...} }
      const applicationsArray = data.applications || data || []
      
      // Transform backend data to frontend format (map _id to id)
      const applications = applicationsArray.map((app: any) => {
        // Extract preferences data
        const preferences = app.preferences || {}
        
        // Extract bookingDates if it exists
        const bookingDates = app.bookingDates || {}
        
        return {
          ...app,
          id: app._id || app.id,
          property_id: app.property?._id || app.property || app.property_id,
          client_id: app.client?._id || app.client || app.client_id,
          landlord_id: app.landlord?._id || app.landlord || app.landlord_id,
          // Extract message from multiple possible sources
          message: app.message || preferences.message || preferences.additionalInfo || app.reviewNotes,
          // Extract dates and duration for long-term rentals
          move_in_date: app.move_in_date || preferences.moveInDate,
          lease_duration: app.lease_duration || preferences.leaseLength || preferences.leaseDuration,
          // Extract booking dates and total amount for short-term rentals
          booking_start_date: app.booking_start_date || preferences.bookingStartDate || bookingDates.start,
          booking_end_date: app.booking_end_date || preferences.bookingEndDate || bookingDates.end,
          total_amount: app.total_amount || preferences.totalAmount || bookingDates.totalAmount,
          // Preserve bookingDates object for direct access
          bookingDates: bookingDates,
          // Extract employment and financial info
          monthly_income: app.monthly_income || app.employment?.monthlyIncome || app.financialInfo?.monthlyIncome,
          employment_status: app.employment_status || app.employment?.employmentType,
          // Extract review/approval date
          reviewedAt: app.reviewedAt || app.reviewed_at,
          reviewed_at: app.reviewedAt || app.reviewed_at,
          // Preserve preferences object
          preferences: preferences,
          // Transform property data
          property: app.property ? {
            ...app.property,
            id: app.property._id || app.property.id,
            rentalType: app.property.rentalType || app.property.rental_type
          } : app.property,
          // Transform client data
          client: app.client ? {
            ...app.client,
            id: app.client._id || app.client.id
          } : app.client
        }
      })

      setState(prev => ({
        ...prev,
        applications: applications,
        loading: { ...prev.loading, applications: false }
      }))
    } catch (error) {
      console.error('Error loading applications:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load applications',
        loading: { ...prev.loading, applications: false }
      }))
    }
  }, [userId])

  const loadPayments = useCallback(async () => {
    if (!userId) return
    setState(prev => ({ ...prev, loading: { ...prev.loading, payments: true }, error: null }))

    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      // Use the payment history endpoint which returns payments for the current user
      const response = await fetch(`${apiUrl}/payments/history`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      })
      if (!response.ok) throw new Error(`Failed to load payments: ${response.statusText}`)
      const data = await response.json()

      // The response structure may be { payments: [...] } or just an array
      const paymentsArray = data.payments || data || []
      
      // Transform backend data to frontend format (map _id to id)
      const payments = paymentsArray.map((payment: any) => ({
        ...payment,
        id: payment._id || payment.id,
        user_id: payment.user?._id || payment.user || payment.user_id,
        application_id: payment.application?._id || payment.application || payment.application_id
      }))

      setState(prev => ({
        ...prev,
        payments: payments,
        loading: { ...prev.loading, payments: false }
      }))
    } catch (error) {
      console.error('Error loading payments:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load payments',
        loading: { ...prev.loading, payments: false }
      }))
    }
  }, [userId])

  const loadTenancies = useCallback(async () => {
    if (!userId) return
    setState(prev => ({ ...prev, loading: { ...prev.loading, tenancies: true }, error: null }))
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/applications/tenancies`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      })
      if (!response.ok) throw new Error('Failed to load tenancies')
      const data = await response.json()
      const list = (data.tenancies || []).map((t: any) => ({
        ...t,
        applicationId: t.applicationId || t.application_id
      }))
      setState(prev => ({ ...prev, tenancies: list, loading: { ...prev.loading, tenancies: false } }))
    } catch (error) {
      console.error('Error loading tenancies:', error)
      setState(prev => ({
        ...prev,
        tenancies: [],
        error: error instanceof Error ? error.message : 'Failed to load tenancies',
        loading: { ...prev.loading, tenancies: false }
      }))
    }
  }, [userId])

  /** ============================
   * STATS (memoized)
   * ============================ */
  const stats: LandlordStats = useMemo(() => {
    const { properties, applications, payments } = state

    const totalProperties = properties.length
    const verifiedProperties = properties.filter(p => p.is_verified === true).length
    const pendingApplications = applications.filter(app => app.status === 'pending').length

    const completedPayments = payments.filter(p => p.status === 'completed')
    const pendingPayments = payments.filter(p => p.status === 'pending')

    const totalRevenue = completedPayments.reduce((sum, p) => sum + (p.amount || 0), 0)
    const totalCommission = completedPayments.reduce((sum, p) => sum + (p.commission_amount || 0), 0)
    const netRevenue = totalRevenue - totalCommission
    const pendingAmount = pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0)

    return {
      totalProperties,
      verifiedProperties,
      pendingApplications,
      totalRevenue,
      netRevenue,
      totalCommission,
      pendingAmount
    }
  }, [state.properties, state.applications, state.payments])

  /** ============================
   * INITIAL LOAD
   * ============================ */
  useEffect(() => {
    if (userId) {
      loadProperties()
      loadPayments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // Load applications when userId or selectedPropertyId changes
  useEffect(() => {
    if (userId) {
      loadApplications(selectedPropertyId || undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedPropertyId])

  /** ============================
   * CRUD FUNCTIONS
   * ============================ */
  const addProperty = useCallback(async (propertyData: Partial<Property>) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/properties`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ ...propertyData, landlord_id: userId })
      })
      if (!response.ok) throw new Error(`Failed to create property: ${response.statusText}`)
      const data = await response.json()

      setState(prev => ({
        ...prev,
        properties: [data, ...prev.properties]
      }))

      return { data, error: null }
    } catch (error) {
      console.error('Error creating property:', error)
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to create property'
      }
    }
  }, [userId])

  const updateProperty = useCallback(async (propertyId: string, updates: Partial<Property>) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/properties/${propertyId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(updates)
      })
      if (!response.ok) throw new Error(`Failed to update property: ${response.statusText}`)
      const data = await response.json()

      // Refresh properties from server to ensure all fields are up-to-date
      // (especially verification status, which might have changed)
      await loadProperties()

      return { data, error: null }
    } catch (error) {
      console.error('Error updating property:', error)
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update property'
      }
    }
  }, [userId, loadProperties])

  const deleteProperty = useCallback(async (propertyId: string) => {
    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/properties/${propertyId}`, { 
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      })
      if (!response.ok) throw new Error(`Failed to delete property: ${response.statusText}`)

      setState(prev => ({
        ...prev,
        properties: prev.properties.filter(p => p.id !== propertyId)
      }))

      return { error: null }
    } catch (error) {
      console.error('Error deleting property:', error)
      return {
        error: error instanceof Error ? error.message : 'Failed to delete property'
      }
    }
  }, [userId])

  const updateApplicationStatus = useCallback(
    async (applicationId: string, status: 'accepted' | 'rejected') => {
      try {
        const apiUrl = getApiUrl()
        const token = localStorage.getItem('authToken')
        const response = await fetch(`${apiUrl}/applications/${applicationId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify({ status })
        })
        if (!response.ok) throw new Error(`Failed to update application: ${response.statusText}`)

        await loadApplications(selectedPropertyId || undefined)
        return { error: null }
      } catch (error) {
        console.error('Error updating application:', error)
        return {
          error: error instanceof Error ? error.message : 'Failed to update application'
        }
      }
    },
    [loadApplications, selectedPropertyId]
  )

  /** ============================
   * REFRESH HELPERS
   * ============================ */
  const refreshAll = useCallback(() => {
    loadProperties()
    loadApplications(selectedPropertyId || undefined)
    loadPayments()
  }, [loadProperties, loadApplications, loadPayments, selectedPropertyId])

  const refreshApplications = useCallback(() => {
    loadApplications(selectedPropertyId || undefined)
  }, [loadApplications, selectedPropertyId])

  /** ============================
   * RETURN
   * ============================ */
  return {
    ...state,
    stats,
    addProperty,
    updateProperty,
    deleteProperty,
    updateApplicationStatus,
    refreshAll,
    refreshApplications,
    loadProperties,
    loadApplications,
    loadPayments,
    loadTenancies
  }
}
